import React from 'react';
import { motion } from 'framer-motion';
import { 
  Facebook, 
  Linkedin, 
  Mail, 
  Phone, 
  MapPin,
  Heart,
  ArrowUp
} from 'lucide-react';
import { Button } from './ui/button';

const Footer: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'Home', id: 'home' },
    { name: 'About', id: 'about' },
    { name: 'Experience', id: 'experience' },
    { name: 'Portfolio', id: 'portfolio' },
    { name: 'Skills', id: 'skills' },
    { name: 'Education', id: 'education' },
    { name: 'Contact', id: 'contact' }
  ];

  const services = [
    'Web Development',
    'Laravel Development', 
    'React Applications',
    'Mobile Apps',
    'API Development',
    'Database Design'
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/mariowroco', label: 'Facebook' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/mario-roco-40b750aa', label: 'LinkedIn' },
    { icon: Mail, href: 'mailto:marioroco2019@gmail.com', label: 'Email' }
  ];

  const contactInfo = [
    { icon: Phone, text: '(+63) 936 904 3438', href: 'tel:+639369043438' },
    { icon: Mail, text: 'marioroco2019@gmail.com', href: 'mailto:marioroco2019@gmail.com' },
    { icon: MapPin, text: 'Puerto Princesa City, Palawan', href: '#' }
  ];

  return (
    <footer className="relative bg-slate-900 text-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full bg-blue-500/10"></div>
      </div>
      
      {/* Main Footer Content */}
      <div className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-12">
            
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-blue-400 mb-4">
                  Mario W. Roco
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  Full-Stack Developer with 5+ years of experience specializing in Laravel, React, Next.js, 
                  and modern web technologies. Based in Puerto Princesa City, Palawan, available for remote work worldwide.
                </p>
              </div>

              {/* Social Links */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4 text-white">Connect With Me</h4>
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => {
                    const IconComponent = social.icon;
                    return (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
                      >
                        <IconComponent className="h-5 w-5" />
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold mb-6 text-white">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-gray-300 hover:text-indigo-400 transition-colors duration-300 text-sm hover:translate-x-1 transform transition-transform"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold mb-6 text-white">Services</h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <span className="text-gray-300 hover:text-indigo-400 transition-colors duration-300 text-sm cursor-pointer hover:translate-x-1 transform transition-transform inline-block">
                      {service}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div> */}

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold mb-6 text-white">Contact Info</h4>
              <ul className="space-y-4">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  return (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 mt-0.5 text-indigo-400 flex-shrink-0">
                        <IconComponent className="h-4 w-4" />
                      </div>
                      {info.href.startsWith('tel:') || info.href.startsWith('mailto:') ? (
                        <a 
                          href={info.href}
                          className="text-gray-300 hover:text-indigo-400 transition-colors duration-300 text-sm"
                        >
                          {info.text}
                        </a>
                      ) : (
                        <span className="text-gray-300 text-sm">{info.text}</span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Back to Top Button */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                viewport={{ once: true }}
                className="mt-8"
              >
                <Button
                  onClick={scrollToTop}
                  variant="outline"
                  size="sm"
                  className="bg-transparent border-indigo-500 text-indigo-400 hover:bg-indigo-500 hover:text-white transition-all duration-300"
                >
                  <ArrowUp className="h-4 w-4 mr-2" />
                  Back to Top
                </Button>
              </motion.div>
            </motion.div>
          </div>

          {/* Bottom Copyright */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-12 pt-8 border-t border-gray-700 text-center"
          >
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                Copyright © 2025 Mario W. Roco. All Rights Reserved
              </p>
              <p className="text-gray-400 text-sm flex items-center">
               {/* Made with <Heart className="h-4 w-4 mx-1 text-red-500" fill="currentColor" />   in Puerto Princesa City, Palawan */}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;